import { InterviewArticleType } from 'components/v2/molecules/article-interview';
import { ArticleCategoryEnum, ArticleTypeEnum } from 'service/article';

const data: InterviewArticleType = {
  id: 'interview-matt',
  title: '「こわい」を無くす資産形成',
  breadcrumbItems: [
    { label: 'ホーム', href: '/about' },
    { label: 'クリエイター', href: '/about/creator' },
    {
      label: '「こわい」を無くす資産形成',
    },
  ],
  isHidingCreatorNameAboveImage: true,
  excerpt: `思考をとめない 自分に合った資産形成`,
  category: ArticleCategoryEnum.Creator,
  type: ArticleTypeEnum.ForCreator,
  date: '2025年11月18日',
  thumbnail: 'https://assets.postprime.com/internals/matt-profile.jpg',
  href: '/about/creator/interview-matt',
  metadata: {
    title: '「こわいを無くす資産形成」マットさんインタビュー | PostPrime',
    description:
      'PostPrimeのTOPクリエイターMattのクリエイターインタビュー。PostPrimeだけでなくnoteなどでも活躍し、講演会依頼がきているMattの投資スタイルやPostPrimeでの彼の活動をインタビューしています。',
    image: 'https://assets.postprime.com/internals/matt-profile.jpg',
    url: 'https://postprime.com/about/creator/interview-matt',
    keywords:
      'PostPrime, マット, クリエイターインタビュー, 投資家, 資産形成, 成長株投資, 半導体エンジニア, コミュニティ',
  },
  readTime: '15分',
  author: {
    name: 'Trung(ちゅん)',
    avatar: 'https://assets.postprime.com/internals/trung.jpg',
    jobTitle: 'CEO',
  },
  content: '',

  // Interview-specific data
  interviewLabel: 'Creator Interview Vol.2',
  heroSubtitle: '思考をとめない 自分に合った資産形成',
  leadText: `社会人として建築士からスタートし、半導体エンジニアに転身。
T型人材を目指し、エンジニアとしての専門性と英語、マネーをバランスよく伸ばす。
投資は「手段」であり「目的」ではない。
思考を止めず、自分に合った資産形成を実践するマットの投資哲学とPostPrimeへの想いを聞いた。`,

  pullQuote: `PostPrimeは 成長できる 株クラ`,
  pullQuoteIndex: 3,
  creatorProfile: {
    name: 'マット',
    title: 'PostPrime トップクリエイター',
    avatar: 'https://assets.postprime.com/internals/matt-profile.jpg',
    profileUrl: 'https://postprime.com/standbyme',
    avatarPortraitHeight: 600,
  },

  questions: [
    {
      id: 'q1',
      questionNumber: '1',
      questionText: '学びの道のり',
      answerText: `
        <p>社会人としてまずは建築士からスタート。その後、一冊の本との出会いをきっかけに、半導体エンジニアに転身しました。</p>
        <p>実は学生時代から「T型人材」を目指していたんです。エンジニアとしての専門性と、左手には英語、右手にはマネー。この３本をバランスよく伸ばしたくて、普段から英語も資産運用もコツコツ勉強していました。</p>
        <p>資産形成の最初の一歩は、ありきたりですが入社してからの自社株の積立です。ただ本格的な個別株投資を始めたのは、社会人になって少し余裕ができてから。初めて"勝負資金"３０万円を使ったときは本当にドキドキしました。</p>
        <p>でも当時は何冊投資本を読んでも勝てる気がしなくて…正直、気づけば「雰囲気で買った」塩漬け銘柄の山になっていたんですよね。</p>
        <p>「このままじゃダメかも」と思っていた自分に、道を示してくれたのが『敗者のゲーム』と『オニールの成長株発掘法』。そこから投資って論理的な"思考"と"リスク管理"がすべてだと気付かされました。</p>
        <p>四半期業績やカタリスト、需給、モメンタム。成長株投資の"型"を身に着けるために、時には損切で手が震えるほど緊張して覚えました。</p>
        <p>"リスクを取らないリスク"を学んでからは、相場がどんなに荒れても思考を止めないこと、チャレンジし続けることの大切さを実感。</p>
        <p>含み損を放置した瞬間が一番危ないと肝に銘じて、今は「損切が怖い」よりも「どう資産を増やすか」の設計図を描く楽しさの方が大きいですね。</p>
      `,
    },
    {
      id: 'q2',
      questionNumber: '2',
      questionText: '投資においてのルーティーン',
      answerText: `
        <p>毎朝5時前後には起きて、ストレッチや自重トレーニング、ランニングで体をほぐしてから、市場チェックが始まります。</p>
        <p>米国市場の値動きや経済指標はもちろん、７時にはYouTubeでその日の考えを配信。この"朝活"が、もう習慣になっています。</p>
        <p>日々の目線は米国株も日本株も「業績の兆し」に注目。ニュースはあくまで補助で、決算・業績こそが主役だと思っています。</p>
        <p>気になる銘柄リストはチャートと出来高からライブ前に最終選抜。その様子をnoteやライブ・動画でもシェアしています。</p>
        <p>夜はPostPrimeやYouTubeでライブ配信、リアルタイムで勘所や相場観を語るのが楽しみなんです。</p>
      `,
    },
    {
      id: 'q3',
      questionNumber: '3',
      questionText: '動画配信において大切にしていること',
      answerText: `
        <p>何よりも「視聴者さんが相場から退場しないこと」が大事だと思っています。一発逆転だけを狙って終わる投資は本当に危ない。</p>
        <p>だからこそ、ライブや動画では暴落時の心構え、外部要因でのセクターの移り変わりやチャートの変化点、業績カタリスト、業界変化の"兆し"をなるべく分かりやすくお伝えしています。</p>
        <p>自分のエンジニアとしての経験も正直に話しますし、ビッグテックや半導体、防衛・工業セクターのトレンドも積極的に解説。時には失敗談や「迷った話」も包み隠さずシェアすることで、"等身大"で一緒に学べる場を目指しています。</p>
      `,
    },
    {
      id: 'q4',
      questionNumber: '4',
      questionText: '投資手法に関して',
      answerText: `
        <p>基本ルールは「マクロや業界のストーリー、個別企業の物語が続く限りはホールド」「短期と長期は絶対に混ぜない」。</p>
        <p>エントリーもイグジットも当初の投資ストーリーどおり。週１回はしっかり戦略の見直しタイムを設けています。</p>
        <p>主軸は「コア・サテライト戦略」です。コアではバリュー株で福利効果をじっくり育てつつ、大型テックで相場全体の成長を押さえる形。</p>
        <p>その上で、コア資金の一部をサテライト投資として成長株・カタリストセクター株へ集中投資。損切が多い月でも再現性を意識しつつ、自分なりの工夫も毎回プラスしています。</p>
      `,
    },
    {
      id: 'q5',
      questionNumber: '5',
      questionText:
        'PostPrimeを含めた自身の投資コミュニティ(コミュニケーション)で気を付けていることはありますか？',
      answerText: `
        <p>PostPrimeは、リアルな投資家心理や現場の空気感、目の前のリスクもそのまま言語化して共有できる大切な場所。</p>
        <p>机上の理屈だけでなく、日々チャートに苦戦している実践者ならではの本音やリアルな経験則をみんなと分かち合いたい。</p>
        <p>そんな思いで、仲間たちと等身大で"感情で負ける日"や"恐怖がまん延する市場"を共有し、一緒に成長できる場にしたいと思っています。</p>
      `,
    },
    {
      id: 'q6',
      questionNumber: '6',
      questionText:
        'PostPrimeの活動や投資において思い入れが高いエピソードがあれば教えてください',
      answerText: `
        <p>ライブ中に「この業界、面白そうだよね」と盛り上がり、その場の空気で新しい投資アイデアや銘柄フィルターが生まれたことも多々。</p>
        <p>「そのセクター、みんなで調べてみよう！」と動いた結果、実際に大きく資産を伸ばせたことは今でも忘れられない思い出です。</p>
      `,
    },
  ],

  keyInsights: {
    title: '実践する投資の3原則',
    items: [
      {
        icon: '💪',
        title: 'リスクを恐れず、下落でも相場に残る',
        description:
          '『リスクを取らないリスク』に基づいて、何もしない不安で立ち止まらず。想定損失やポジションサイズをしっかり"自分ごと"で設計しています。恐怖は敵ではなく次のポジションの"材料"です。',
      },
      {
        icon: '🎯',
        title: 'ノイズを排除し、兆しに集中',
        description:
          '大口投資家や政治・スキャンダルなどの雑音は脇に置き、「兆し」「カタリスト」に全集中！チャートとファンダメンタルズの両輪で、ブレない投資を目指しています。たいていスイングトレードの最大の損失は"急ぎすぎたこと"から始まるので、待つ握力が大切。',
      },
      {
        icon: '📐',
        title: 'ルールに従い、一貫性を持つ',
        description:
          '「ルールこそ武器」です。例えば損切り７％、利確は１/３、再現できるパターンを毎回守る。短期と長期は混ぜず、何がノイズかを自分で毎回見極めつづけています。予想や感覚ではなく、「市場が間違っているのではなく　私が間違っている？」と振り返り、訂正の速さが大切。',
      },
    ],
  },

  ctaSection: {
    title: 'マットさんをフォローして\n思考を止めない資産形成を学ぼう',
    description: '毎日の市場分析と実践的な投資戦略を無料で学べます',
    buttonText: '今すぐフォローする',
    buttonUrl: 'https://postprime.com/standbyme',
  },
};

export default data;
